import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserById, updateUser, getDocumentUrl, getDocuments } from "../Auth";
import { DocumentsIcon, CloseIcon, ArrowLeftIcon } from "../Icons";

const ViewDocuments = () => {
  const { id, credential } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    file: null,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Load employee and documents
  useEffect(() => {
    const loadEmployee = async () => {
      setLoading(true);
      setError("");

      try {
        const found = await getUserById(id);
        if (!found) {
          setError("Employee not found");
          setLoading(false);
          return;
        }

        const credentialName = decodeURIComponent(credential || "");
        if (credentialName) {
          try {
            const gridfsDocs = await getDocuments(id, credentialName);

            // Map documents by fileId or _id
            const docMap = new Map();
            gridfsDocs.forEach((doc) => {
              const fileId = doc.fileId || doc._id;
              docMap.set(fileId, doc);
              docMap.set(doc._id, doc);
            });

            // Merge files
            const credentialFiles =
              found.credentials?.[credentialName]?.files || [];
            console.log("📥 Loading files from credential:", {
              credentialName,
              filesCount: credentialFiles.length,
              files: credentialFiles.map((f) => ({
                name: typeof f === "string" ? f : f.name,
                hasId: !!(
                  typeof f === "object" &&
                  f &&
                  (f.id || f.fileId || f._id)
                ),
                hasData: !!(typeof f === "object" && f && f.data),
                type: typeof f,
                keys: typeof f === "object" && f ? Object.keys(f) : [],
              })),
            });

            const mergedFiles = credentialFiles.map((file, index) => {
              console.log(`🔍 Processing file ${index}:`, file);

              // Handle string files (old format)
              if (typeof file === "string") {
                console.log(`⚠️ File ${index} is a string (old format):`, file);
                // Try to find in GridFS documents by name
                const docByName = gridfsDocs.find((d) => d.name === file);
                if (docByName) {
                  console.log(
                    `✅ Found GridFS document by name for "${file}":`,
                    docByName,
                  );
                  return {
                    id: docByName.fileId || docByName._id,
                    fileId: docByName.fileId || docByName._id,
                    _id: docByName._id,
                    name: docByName.name,
                    type: docByName.type,
                    size: docByName.size,
                    uploadedAt: docByName.uploadedAt,
                    isDocument: true,
                  };
                }
                return {
                  name: file,
                  data: "",
                  type: "",
                  size: 0,
                  isDocument: false,
                };
              }

              const gridfsId = file.id || file.fileId || file._id;
              console.log(`🔍 File ${index} gridfsId:`, gridfsId);

              if (gridfsId && docMap.has(gridfsId)) {
                const doc = docMap.get(gridfsId);
                console.log(`✅ File ${index} found in docMap:`, doc);
                return {
                  id: doc.fileId || doc._id,
                  fileId: doc.fileId || doc._id,
                  _id: doc._id,
                  name: doc.name,
                  type: doc.type,
                  size: doc.size,
                  uploadedAt: doc.uploadedAt,
                  isDocument: true,
                };
              }

              // If GridFS id exists but no doc found in map, still use it
              if (gridfsId) {
                console.log(
                  `⚠️ File ${index} has gridfsId but not in docMap, using it anyway:`,
                  gridfsId,
                );
                return {
                  ...file,
                  id: gridfsId,
                  fileId: gridfsId,
                  isDocument: true,
                };
              }

              // File has no id - try to find in GridFS by name
              if (file.name) {
                const docByName = gridfsDocs.find((d) => d.name === file.name);
                if (docByName) {
                  console.log(
                    `✅ Found GridFS document by name for "${file.name}":`,
                    docByName,
                  );
                  return {
                    id: docByName.fileId || docByName._id,
                    fileId: docByName.fileId || docByName._id,
                    _id: docByName._id,
                    name: docByName.name,
                    type: docByName.type || file.type,
                    size: docByName.size || file.size,
                    uploadedAt: docByName.uploadedAt || file.uploadedAt,
                    isDocument: true,
                  };
                }
              }

              // Base64 file or file without id
              console.log(
                `⚠️ File ${index} has no gridfsId and not found in GridFS, checking for data:`,
                {
                  hasData: !!file.data,
                  dataLength: file.data?.length || 0,
                },
              );
              return {
                ...file,
                isDocument: file.data && file.data.length > 0 ? false : true,
              };
            });

            console.log(
              "✅ Merged files result:",
              mergedFiles.map((f, i) => ({
                index: i,
                name: f.name,
                hasId: !!(f.id || f.fileId || f._id),
                hasData: !!f.data,
                id: f.id || f.fileId || f._id || "MISSING",
              })),
            );

            // Update employee credentials
            if (found.credentials && found.credentials[credentialName]) {
              found.credentials[credentialName] = {
                ...found.credentials[credentialName],
                files: mergedFiles,
              };
            }
          } catch (docError) {
            console.error("Error fetching GridFS documents:", docError);
          }
        }

        setEmployee(found);
      } catch (err) {
        setError(err.message || "Failed to load employee");
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [id, credential]);

  // Remove file
  const handleRemoveFile = async (file) => {
    if (!employee || !credential) return;
    const credentialName = decodeURIComponent(credential || "");
    const newFiles = (employee.credentials[credentialName]?.files || []).filter(
      (f) => f !== file && f.name !== file.name,
    );

    setEmployee((prev) => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [credentialName]: {
          ...prev.credentials[credentialName],
          files: newFiles,
        },
      },
    }));

    try {
      const updatedCredentials = {
        ...employee.credentials,
        [credentialName]: {
          ...employee.credentials[credentialName],
          files: newFiles,
        },
      };
      await updateUser(id, { credentials: updatedCredentials });
    } catch (err) {
      console.error("Failed to remove file:", err);
    }
  };

  // Get file URL
  const getFileUrl = (file) => {
    // Debug: Log file structure to diagnose the issue
    console.log("🔍 getFileUrl - File object:", file);
    console.log("🔍 getFileUrl - File type:", typeof file);
    console.log(
      "🔍 getFileUrl - File keys:",
      file ? Object.keys(file) : "null",
    );
    console.log("🔍 getFileUrl - File values:", {
      id: file?.id,
      fileId: file?.fileId,
      _id: file?._id,
      hasData: !!file?.data,
      dataLength: file?.data?.length || 0,
      name: file?.name,
      isDocument: file?.isDocument,
    });

    const fileId = file?.id || file?.fileId || file?._id;

    if (fileId) {
      // Use fileId directly for GridFS download
      const url = getDocumentUrl(fileId);
      console.log("✅ getFileUrl - Found fileId:", fileId, "→ URL:", url);
      return url;
    }

    // Fallback for old base64 files
    if (file?.data) {
      const dataUrl = `data:${file.type || "application/pdf"};base64,${file.data}`;
      console.log("✅ getFileUrl - Using base64 data URL");
      return dataUrl;
    }

    console.error(
      "❌ getFileUrl - File has no id/fileId/_id and no data. Cannot view file.",
    );
    console.error("❌ Full file object:", JSON.stringify(file, null, 2));
    return null; // file cannot be viewed
  };

  const getFileName = (file) =>
    typeof file === "string" ? file : file.name || "Unknown file";

  if (loading)
    return <div className="p-6 text-gray-600">Loading documents...</div>;
  if (error || !employee)
    return (
      <div className="p-6 text-red-600">
        {error || "Employee not found"}
        <button
          onClick={() => navigate(-1)}
          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded"
        >
          Back
        </button>
      </div>
    );

  const credentialName = decodeURIComponent(credential || "");
  const files = employee.credentials?.[credentialName]?.files || [];

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeftIcon /> Back
        </button>
        <h1 className="text-3xl font-bold text-gray-800">Uploaded Documents</h1>
        <p className="text-gray-600 mt-2">
          {employee.firstName} {employee.lastName} - {credentialName}
        </p>
      </div>

      {files.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <DocumentsIcon className="text-6xl mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">
            No documents uploaded for this credential
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {files.length} Document{files.length !== 1 ? "s" : ""} Uploaded
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {files.map((file, idx) => {
              const fileName = getFileName(file);
              const fileUrl = getFileUrl(file);

              return (
                <div
                  key={idx}
                  className="p-6 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <DocumentsIcon className="text-blue-600 text-2xl flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 font-medium truncate">
                        {fileName}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {file.size
                          ? `Size: ${(file.size / 1024).toFixed(2)} KB`
                          : "Document file"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        if (fileUrl) window.open(fileUrl, "_blank");
                        else {
                          setAlertMessage(
                            `File data not available for: ${fileName}. Please re-upload this document.`,
                          );
                          setShowAlert(true);
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      View
                    </button>
                    <button
                      onClick={() => setConfirmDelete({ show: true, file })}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-2"
                    >
                      <CloseIcon /> Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {confirmDelete.show && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <CloseIcon className="text-red-600 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Are you sure?
            </h3>
            <p className="text-gray-600 mb-6">
              Remove{" "}
              <span className="font-semibold">
                {confirmDelete.file
                  ? getFileName(confirmDelete.file)
                  : "this file"}
              </span>
              ?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setConfirmDelete({ show: false, file: null })}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleRemoveFile(confirmDelete.file);
                  setConfirmDelete({ show: false, file: null });
                }}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Notification
            </h3>
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {alertMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowAlert(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDocuments;